// ===========================================
// SMS Template Manager Card Component
// ===========================================
// Lists all SMS/WhatsApp templates with enable/disable and edit functionality

import { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Box,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Stack,
  Snackbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getSmsTemplates, toggleSmsTemplate, SmsTemplate } from '../../../api/smsTemplate.api';
import SmsTemplateEditorDialog from './SmsTemplateEditorDialog';

export default function SmsTemplateManagerCard({ readOnly = false }: { readOnly?: boolean }) {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string | null>(null);

  // Fetch all templates
  const { data: templates, isLoading, error } = useQuery({
    queryKey: ['smsTemplates'],
    queryFn: getSmsTemplates,
  });

  // Toggle template mutation
  const toggleMutation = useMutation({
    mutationFn: ({ templateKey, enabled }: { templateKey: string; enabled: boolean }) =>
      toggleSmsTemplate(templateKey, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['smsTemplates'] });
      setSnackbar({ message: 'Template status updated', severity: 'success' });
    },
    onError: () => {
      setSnackbar({ message: 'Failed to update template status', severity: 'error' });
    },
  });

  // Group templates by category
  const groupedTemplates = templates?.reduce((acc, template) => {
    const category = template.category || 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(template);
    return acc;
  }, {} as Record<string, SmsTemplate[]>) || {};

  const handleToggle = (templateKey: string, currentEnabled: boolean) => {
    toggleMutation.mutate({ templateKey, enabled: !currentEnabled });
  };

  const handleEdit = (templateKey: string) => {
    setSelectedTemplateKey(templateKey);
    setEditorOpen(true);
  };

  const handleEditorClose = () => {
    setEditorOpen(false);
    setSelectedTemplateKey(null);
  };

  const handleEditorSave = () => {
    queryClient.invalidateQueries({ queryKey: ['smsTemplates'] });
    setEditorOpen(false);
    setSelectedTemplateKey(null);
    setSnackbar({ message: 'Template updated successfully', severity: 'success' });
  };

  /**
   * Get color for SMS segment count badge
   */
  const getSegmentColor = (segments: number): 'success' | 'warning' | 'error' => {
    if (segments <= 1) return 'success';
    if (segments <= 3) return 'warning';
    return 'error';
  };

  if (isLoading) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Alert severity="error">Failed to load SMS templates. Please try again.</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Message Templates
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Customize the messages sent via SMS and WhatsApp. Both channels use the same message text.
            Toggle templates on/off to control which notifications are sent.
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            SMS messages are billed per 160-character segment (70 for messages with emoji/Unicode).
            WhatsApp messages have a 1,024 character limit.
          </Alert>

          {Object.entries(groupedTemplates).map(([category, categoryTemplates]) => (
            <Accordion
              key={category}
              expanded={expandedCategory === category}
              onChange={() => setExpandedCategory(expandedCategory === category ? null : category)}
              variant="outlined"
              disableGutters
              sx={{ '&:not(:last-child)': { mb: 1 } }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" fontWeight={600}>
                    {category}
                  </Typography>
                  <Chip
                    label={`${categoryTemplates.length} template${categoryTemplates.length !== 1 ? 's' : ''}`}
                    size="small"
                    variant="outlined"
                  />
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 0 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Template</TableCell>
                        {!isMobile && <TableCell>SMS Segments</TableCell>}
                        {!isMobile && <TableCell>Status</TableCell>}
                        <TableCell align="center">Enabled</TableCell>
                        <TableCell align="center">Edit</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {categoryTemplates.map((template) => (
                        <TableRow key={template.key} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {template.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {template.description}
                            </Typography>
                          </TableCell>
                          {!isMobile && (
                            <TableCell>
                              <Tooltip
                                title={
                                  template.characterInfo.isUnicode
                                    ? `${template.characterInfo.length} chars (Unicode: ${template.characterInfo.charsPerSegment} chars/segment)`
                                    : `${template.characterInfo.length} chars (${template.characterInfo.charsPerSegment} chars/segment)`
                                }
                              >
                                <Chip
                                  label={`${template.characterInfo.smsSegments} segment${template.characterInfo.smsSegments !== 1 ? 's' : ''}`}
                                  size="small"
                                  color={getSegmentColor(template.characterInfo.smsSegments)}
                                  variant="outlined"
                                />
                              </Tooltip>
                            </TableCell>
                          )}
                          {!isMobile && (
                            <TableCell>
                              {template.isCustomized ? (
                                <Chip label="Customized" size="small" color="primary" variant="outlined" />
                              ) : (
                                <Chip label="Default" size="small" variant="outlined" />
                              )}
                            </TableCell>
                          )}
                          <TableCell align="center">
                            <Switch
                              checked={template.enabled}
                              onChange={() => handleToggle(template.key, template.enabled)}
                              size="small"
                              disabled={readOnly || toggleMutation.isPending}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {!readOnly && <Tooltip title="Edit message">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(template.key)}
                                color="primary"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))}
        </CardContent>
      </Card>

      {selectedTemplateKey && (
        <SmsTemplateEditorDialog
          open={editorOpen}
          templateKey={selectedTemplateKey}
          onClose={handleEditorClose}
          onSave={handleEditorSave}
        />
      )}

      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={() => setSnackbar(null)}
      >
        <Alert
          onClose={() => setSnackbar(null)}
          severity={snackbar?.severity || 'success'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </>
  );
}
